import classNames from 'classnames'
import React, { AnchorHTMLAttributes, ButtonHTMLAttributes, ComponentType, HTMLAttributes, MouseEvent } from 'react'

import { IconProps } from '../icon/icon.js'
import { Spinner, SpinnerProps } from '../spinner/spinner.js'

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary'

export interface ButtonBaseProps {
    text: string
    ariaLabel?: string
    buttonRef?: (element: HTMLButtonElement | HTMLAnchorElement | HTMLSpanElement | null) => void
    className?: string
    textClassName?: string
    id?: string
    isBlock?: boolean | 'xs'
    isLarge?: boolean
    isDisabled?: boolean
    isLabelHidden?: boolean
    isLoading?: boolean
    icon?: ComponentType<IconProps>
    iconClassName?: string
    iconRight?: ComponentType<IconProps>
    iconLeft?: ComponentType<IconProps>
    onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
    style?: 'link' | 'underlined'
    target?: string
    type?: ButtonHTMLAttributes<HTMLButtonElement>['type']
    variant?: ButtonVariant
}

export interface ButtonSpanProps extends ButtonBaseProps {
    attributes?: HTMLAttributes<HTMLSpanElement>
    element?: 'span'
    url?: never
}

export interface ButtonAnchorProps extends ButtonBaseProps {
    attributes?: AnchorHTMLAttributes<HTMLAnchorElement>
    element?: 'a'
    url: string
}

export interface ButtonButtonProps extends ButtonBaseProps {
    attributes?: ButtonHTMLAttributes<HTMLButtonElement>
    element?: 'button'
    url?: never
}

export type ButtonProps = ButtonAnchorProps | ButtonSpanProps | ButtonButtonProps

export const Button = (props: ButtonProps): JSX.Element => {
    const { variant = 'primary' } = props

    const className = classNames(
        'button',
        {
            'button--block': typeof props.isBlock === 'boolean' && props.isBlock,
            [`button--block-${'' + props.isBlock}`]: typeof props.isBlock === 'string',
            'button--hidden-label': props.isLabelHidden,
            'button--with-icon': props.icon,
            [`button--style-${props.style}`]: !!props.style,
            [`button--variant-${variant}`]: variant,
        },
        props.className,
    )

    const buttonTextClassName = classNames(
        'button__text',
        {
            'text-large': props.isLarge,
        },
        props.textClassName,
    )

    const getSpinnerVariant: () => SpinnerProps['variant'] = () => {
        if (!props.style) {
            switch (variant) {
                case 'primary':
                case 'tertiary':
                    return 'light'
                default:
                    return 'inherit'
            }
        }

        return 'inherit'
    }

    const renderContent: () => JSX.Element = () => (
        <>
            {props.isLoading && <Spinner className="button__spinner" variant={getSpinnerVariant()} />}
            <span className="button__inner">
                {props.icon && <props.icon className={classNames('button__icon', props.iconClassName)} />}
                {props.iconLeft && (
                    <props.iconLeft className={classNames('button__icon button__icon--left', props.iconClassName)} />
                )}
                <span className={buttonTextClassName}>{props.text}</span>
                {props.iconRight && (
                    <props.iconRight className={classNames('button__icon button__icon--right', props.iconClassName)} />
                )}
            </span>
        </>
    )

    const renderAnchor: (anchorProps: ButtonAnchorProps) => JSX.Element = (anchorProps: ButtonAnchorProps) => (
        <a
            {...anchorProps.attributes}
            // common attributes of a, span, button
            className={className}
            id={anchorProps.id}
            ref={anchorProps.buttonRef}
            aria-label={anchorProps.ariaLabel}
            // common attributes of a, button
            onClick={anchorProps.onClick}
            // a specific attributes
            href={anchorProps.url}
            target={anchorProps.target}
        >
            {renderContent()}
        </a>
    )

    const renderSpan: (spanProps: ButtonSpanProps) => JSX.Element = (spanProps: ButtonSpanProps) => (
        <span
            {...spanProps.attributes}
            // common attributes of a, span, button
            className={className}
            id={spanProps.id}
            ref={spanProps.buttonRef}
            aria-label={spanProps.ariaLabel}
        >
            {renderContent()}
        </span>
    )

    const renderButton: (buttonProps: ButtonButtonProps) => JSX.Element = (buttonProps: ButtonButtonProps) => (
        <button
            {...buttonProps.attributes}
            // common attributes of a, span, button
            className={className}
            id={buttonProps.id}
            ref={buttonProps.buttonRef}
            aria-label={buttonProps.ariaLabel}
            // common attributes of a, button
            onClick={buttonProps.onClick}
            // button specific attributes
            type={buttonProps.attributes?.type ? buttonProps.attributes.type : 'button'}
            disabled={buttonProps.isDisabled}
        >
            {renderContent()}
        </button>
    )

    if (props.element === 'span') {
        return renderSpan(props)
    }

    if (props.url) {
        if (props.isDisabled) {
            return renderButton(props as unknown as ButtonButtonProps)
        }

        return renderAnchor(props)
    }

    return renderButton(props as ButtonButtonProps)
}
